import { useTranslate } from '@tolgee/react';
import ModalBody from '../../../components/modals/modalBody';
import { useSelector } from 'react-redux';
import { dispatch } from '../../../redux/store';
import {
  setShowPostBoost,
  updatePostBoostLoading,
  setSameBoostForAll,
  clearInactiveBoostConfigs,
  deleteBoostConfig,
  setConfigAlreadySaved,
  updatePostBoostConfig
} from '../../../redux/slices/lomavisCreator/lomavisCreator';
import { PostBoostCard } from './PostBoostCard';
import { LomavisDisabledElementOverlay1 } from '../../../lomavisSharedUi/atoms/overlays/LomavisDisabledElementOverlay1';
import { ConfirmationModal } from '../../../components/lomavisCreator/components/confirmationModal';
import { lomavisShowToastr } from '../../../utils/ui/toastr';
import { useCallback, useState, useRef, useEffect } from 'react';
import { FACEBOOK, INSTAGRAM } from '../../../config/constants';
import { validateAllBoostConfigs } from '../../../utils/postBoostValidation';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

export const PostBoostModal: React.FC = () => {
  // ============================================================================
  // HOOKS & STATE
  // ============================================================================
  const { t } = useTranslate();
  const {
    postBoost: {
      loading,
      boost_config,
      ui: { sameBoostForAll },
      configAlreadySaved
    },
    platformEnabled,
    has_been_published,
    lomavisCreatorConfig,
    instagram_direct_connect
  } = useSelector((state: any) => state.lomavisCreator);

  // State for validation errors
  const [validationErrors, setValidationErrors] = useState<{
    [platform: string]: string[];
  }>({});

  // State for delete confirmation modal
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  // Ref for error section to scroll to
  const errorSectionRef = useRef<HTMLDivElement>(null);

  // Show only enabled boost platforms (Facebook and Instagram)
  const boostPlatforms = [FACEBOOK, INSTAGRAM].filter((platform) => {
    if (platform === INSTAGRAM && instagram_direct_connect) {
      return false;
    }
    return platformEnabled[platform];
  });

  // Meta Ads validation and prefill
  const metaAdsData = lomavisCreatorConfig?.meta_ads_data;
  const isMetaAdAccountConnected = metaAdsData?.ad_account_connected || false;
  const prefillBeneficiary = metaAdsData?.beneficiary || '';

  // Prefill beneficiary on component mount if needed
  useEffect(() => {
    // Only prefill if we have a prefill value and some platforms need it
    if (prefillBeneficiary && boostPlatforms.length > 0) {
      const needsPrefill = boostPlatforms.some((platform) => {
        const platformConfig = boost_config[platform];
        return (
          !platformConfig?.beneficiary ||
          platformConfig.beneficiary.trim() === ''
        );
      });

      if (needsPrefill) {
        console.log('🔄 Prefilling beneficiary:', prefillBeneficiary);
        boostPlatforms.forEach((platform) => {
          const platformConfig = boost_config[platform];
          if (
            !platformConfig?.beneficiary ||
            platformConfig.beneficiary.trim() === ''
          ) {
            dispatch(
              updatePostBoostConfig({
                platform,
                attribute: 'beneficiary',
                value: prefillBeneficiary
              })
            );
          }
        });
      }
    }
  }, [boostPlatforms.length, prefillBeneficiary]); // Only depend on platform count and prefill value

  // Determine delete button visibility and state based on publication status and saved config
  const shouldShowDeleteButton = !has_been_published || configAlreadySaved;
  const shouldDisableDeleteButton = has_been_published && configAlreadySaved;

  // Console log current state for debugging
  console.log('🎯 PostBoostModal State:', {
    has_been_published,
    configAlreadySaved,
    shouldShowDeleteButton,
    shouldDisableDeleteButton,
    boost_config,
    boostPlatforms,
    isMetaAdAccountConnected,
    prefillBeneficiary
  });

  // Scroll to error section when validation errors appear
  useEffect(() => {
    if (Object.keys(validationErrors).length > 0 && errorSectionRef.current) {
      errorSectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }, [validationErrors]);

  // ============================================================================
  // VALIDATION LOGIC
  // ============================================================================
  const validateAllPlatforms = useCallback((): boolean => {
    // Check if at least one platform has active boost configuration
    return boostPlatforms.some((platform) => {
      const platformConfig = boost_config[platform];
      return (
        platformConfig &&
        platformConfig.active &&
        (platformConfig.goal ||
          platformConfig.beneficiary ||
          platformConfig.budget ||
          platformConfig.days)
      );
    });
  }, [boost_config, boostPlatforms]);

  // Deep comparison function to check if two boost configs are identical
  const compareBoostConfigs = useCallback(
    (config1: any, config2: any): boolean => {
      // If both are null/undefined, they're equal
      if (!config1 && !config2) return true;
      if (!config1 || !config2) return false;

      // Compare all relevant fields
      const fieldsToCompare = [
        'active',
        'goal',
        'beneficiary',
        'budget',
        'days',
        'special_requirements',
        'interests',
        'countries',
        'cities',
        'gender',
        'min_age',
        'max_age'
      ];

      return fieldsToCompare.every((field) => {
        const val1 = config1[field];
        const val2 = config2[field];

        // Handle arrays (interests, countries, cities, gender)
        if (Array.isArray(val1) || Array.isArray(val2)) {
          if (!Array.isArray(val1) || !Array.isArray(val2)) return false;
          if (val1.length !== val2.length) return false;

          // Sort and compare array contents with consistent string comparison
          const sorted1 = [...val1].sort((a, b) => {
            const idA = String(a.id || a);
            const idB = String(b.id || b);
            return idA.localeCompare(idB);
          });
          const sorted2 = [...val2].sort((a, b) => {
            const idA = String(a.id || a);
            const idB = String(b.id || b);
            return idA.localeCompare(idB);
          });

          return sorted1.every((item, index) => {
            const item2 = sorted2[index];
            if (typeof item === 'object' && typeof item2 === 'object') {
              return item.id === item2.id && item.name === item2.name;
            }
            return item === item2;
          });
        }

        // Handle objects (like goal)
        if (
          typeof val1 === 'object' &&
          val1 !== null &&
          typeof val2 === 'object' &&
          val2 !== null
        ) {
          return val1.id === val2.id && val1.name === val2.name;
        }

        // Handle primitive values
        return val1 === val2;
      });
    },
    []
  );

  const validatePlatformConfigsMatch = useCallback((): boolean => {
    // Only validate if both Facebook and Instagram are enabled
    if (!platformEnabled[FACEBOOK] || !platformEnabled[INSTAGRAM]) {
      return true; // Skip validation if either platform is not enabled
    }

    const facebookConfig = boost_config[FACEBOOK];
    const instagramConfig = boost_config[INSTAGRAM];

    // Only compare if both platforms have active boost configurations
    const isFacebookActive = facebookConfig && facebookConfig.active;
    const isInstagramActive = instagramConfig && instagramConfig.active;

    // If neither platform has active boost, they're considered "matching"
    if (!isFacebookActive && !isInstagramActive) {
      return true;
    }

    // If only one platform is active, return false
    if (isFacebookActive !== isInstagramActive) {
      return false;
    }

    // If both are active, compare their configurations
    return compareBoostConfigs(facebookConfig, instagramConfig);
  }, [boost_config, compareBoostConfigs, platformEnabled]);

  // ============================================================================
  // ACTION HANDLERS
  // ============================================================================
  const handleSave = useCallback(() => {
    // Clear previous validation errors
    setValidationErrors({});

    // Use centralized validation
    const validation = validateAllBoostConfigs(
      boost_config,
      platformEnabled,
      t,
      sameBoostForAll
    );

    if (!validation.isValid) {
      setValidationErrors(validation.errors);

      if (Object.keys(validation.errors).length > 0) {
        lomavisShowToastr(
          t('lomavisCreator.postBoost.validation.requiredFieldsMissing'),
          'error'
        );
      } else if (!validation.hasActiveConfigs) {
        lomavisShowToastr(
          t('lomavisCreator.postBoost.validation.atLeastOneActiveBoost'),
          'error'
        );
      }
      return;
    }

    // Clean up inactive boost configurations before saving
    dispatch(clearInactiveBoostConfigs());

    dispatch(updatePostBoostLoading(true));
    // Simulate API call
    setTimeout(() => {
      // Set flag to indicate config has been saved
      dispatch(setConfigAlreadySaved(true));
      console.log(
        '💾 handleSave - Config saved, setting configAlreadySaved to true'
      );

      lomavisShowToastr(t('lomavisCreator.postBoost.saved'), 'success');
      dispatch(updatePostBoostLoading(false));
      dispatch(setShowPostBoost(false));
    }, 1000);
  }, [boost_config, platformEnabled, t, sameBoostForAll]);

  const handleModalClose = useCallback(() => {
    dispatch(setShowPostBoost(false));
  }, []);

  const handleToggleSameBoostForAll = useCallback(() => {
    // If we're trying to enable "same boost for all platforms"
    if (!sameBoostForAll) {
      // Only check if Facebook and Instagram configs are identical when both are enabled
      if (
        platformEnabled[FACEBOOK] &&
        platformEnabled[INSTAGRAM] &&
        !validatePlatformConfigsMatch()
      ) {
        const errorMessage =
          t('lomavisCreator.postBoost.validation.platformConfigsMismatch') ||
          'Facebook and Instagram boost configurations must be identical to enable "Same boost for all platforms". Please make sure both platforms have matching settings.';

        lomavisShowToastr(errorMessage, 'error');
        return;
      }
    }

    dispatch(setSameBoostForAll(!sameBoostForAll));
  }, [sameBoostForAll, validatePlatformConfigsMatch, platformEnabled, t]);

  const handleDeleteAllBoostConfigs = useCallback(() => {
    console.log('🗑️ handleDeleteAllBoostConfigs called - Current state:', {
      has_been_published,
      configAlreadySaved,
      boostPlatforms
    });

    // Check if post is published
    if (has_been_published) {
      console.log('❌ Cannot delete - post is published');
      lomavisShowToastr(
        t('lomavisCreator.postBoost.cannotDeletePublished'),
        'error'
      );
      return;
    }

    // Show confirmation modal
    setShowDeleteConfirmation(true);
  }, [boostPlatforms, has_been_published, t]);

  const handleConfirmDelete = useCallback(() => {
    // Delete boost configs for all enabled platforms
    dispatch(deleteBoostConfig({ platforms: boostPlatforms }));

    // Set flag to indicate no config is saved anymore
    dispatch(setConfigAlreadySaved(false));
    console.log(
      '✅ handleConfirmDelete - Deleted configs, setting configAlreadySaved to false'
    );

    // Close confirmation modal
    setShowDeleteConfirmation(false);

    // Show success message
    lomavisShowToastr(
      t('lomavisCreator.postBoost.allBoostConfigsDeleted'),
      'success'
    );
  }, [boostPlatforms, t]);

  const handleCancelDelete = useCallback(() => {
    setShowDeleteConfirmation(false);
  }, []);

  useEffect(() => {
    if (instagram_direct_connect) {
      dispatch(setSameBoostForAll(false));
    }
  }, []);

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <ModalBody onClose={handleModalClose}>
      <div className="h-100 d-flex flex-column">
        <div className="row mb-5">
          <div className="col-12">
            <div className="card d-flex justify-content-between flex-row align-items-center p-4">
              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip>
                    {t('lomavisCreator.boost_post.title_tooltip')}
                  </Tooltip>
                }
              >
                <h2>
                  <i className="far fa-rectangle-ad me-2"></i>
                  {t('lomavisCreator.boost_post.title')}
                </h2>
              </OverlayTrigger>

              {/* Same boost for all platforms toggle */}
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="same-boost-for-all-toggle"
                  checked={sameBoostForAll}
                  onChange={handleToggleSameBoostForAll}
                  disabled={loading}
                />
                <label
                  className="form-check-label ms-2"
                  htmlFor="same-boost-for-all-toggle"
                >
                  {t('lomavisCreator.postBoost.sameBoostForAllPlatforms')}
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className="row flex-grow-1 overflow-auto">
          <div
            className="col-12 d-flex flex-column justify-content-center min-vh-0 py-4"
            style={{ minHeight: '100%' }}
          >
            <div className="flex-shrink-0">
              {/* Display instagram direct connect error */}
              {instagram_direct_connect && (
                <div className="card mb-4">
                  <div className="card-body">
                    <div className="alert alert-danger">
                      <div className="mb-2">
                        <span className="mb-0 mt-1">
                          {t('lomavisCreator.postBoost.IG_direct_connect')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Display validation errors */}
              {Object.keys(validationErrors).length > 0 && (
                <div className="card mb-4" ref={errorSectionRef}>
                  <div className="card-body">
                    <div className="alert alert-danger">
                      <h6 className="alert-heading">
                        {t('lomavisCreator.postBoost.validation.errorsFound') ||
                          'Please fix the following errors:'}
                      </h6>
                      {sameBoostForAll ? (
                        // When same boost for all platforms is enabled, show unique errors without platform labels
                        <ul className="mb-0">
                          {Array.from(
                            new Set(Object.values(validationErrors).flat())
                          ).map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      ) : (
                        // When platforms have separate configs, show platform-specific errors
                        Object.entries(validationErrors).map(
                          ([platform, errors]) => (
                            <div key={platform} className="mb-2">
                              <strong>
                                {platform === FACEBOOK
                                  ? 'Facebook'
                                  : 'Instagram'}
                                :
                              </strong>
                              <ul className="mb-0 mt-1">
                                {errors.map((error, index) => (
                                  <li key={index}>{error}</li>
                                ))}
                              </ul>
                            </div>
                          )
                        )
                      )}
                    </div>
                  </div>
                </div>
              )}

              {sameBoostForAll ? (
                /* Single unified Platform Card with multiple platforms */
                <PostBoostCard
                  platforms={boostPlatforms}
                  platformConfigs={boost_config}
                />
              ) : (
                /* Individual Platform Cards */
                boostPlatforms.map((platform) => (
                  <PostBoostCard
                    key={platform}
                    platforms={[platform]}
                    platformConfigs={boost_config}
                  />
                ))
              )}

              {boostPlatforms.length === 0 && (
                <div className="card p-4">
                  <div className="alert alert-info">
                    <i className="fas fa-info-circle me-2"></i>
                    {t('lomavisCreator.postBoost.noPlatformsAvailable')}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="mt-2 p-4 card">
          <div className="d-flex justify-content-between">
            <div>
              {/* Delete Button - Show only when not published OR when published with config */}
              {shouldShowDeleteButton && (
                <>
                  {shouldDisableDeleteButton ? (
                    <LomavisDisabledElementOverlay1
                      tooltipPosition="up"
                      tooltipText={t(
                        'lomavisCreator.postBoost.cannotDeletePublished'
                      )}
                      tooltipContent={
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          disabled
                        >
                          <i className="fas fa-trash-alt me-2"></i>
                          {t('lomavisCreator.postBoost.deleteAll')}
                        </button>
                      }
                    />
                  ) : (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={handleDeleteAllBoostConfigs}
                      disabled={loading}
                      title={t('lomavisCreator.postBoost.deleteAllTooltip')}
                    >
                      <i className="fas fa-trash-alt me-2"></i>
                      {t('lomavisCreator.postBoost.deleteAll')}
                    </button>
                  )}
                </>
              )}
            </div>

            <div>
              <button
                type="button"
                className="btn btn-secondary btn-sm me-2"
                onClick={handleModalClose}
                disabled={loading}
              >
                {t('lomavisCreator.postBoost.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleSave}
                disabled={loading}
              >
                {loading ? (
                  <div
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                ) : (
                  <i className="far fa-rectangle-ad me-2"></i>
                )}
                {t('lomavisCreator.postBoost.boostPost')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        show={showDeleteConfirmation}
        title={t('lomavisCreator.postBoost.deleteConfirmationTitle')}
        body={t('lomavisCreator.postBoost.deleteAllConfirmation')}
        primaryText={t('lomavisCreator.postBoost.deleteConfirmButton')}
        secondaryText={t('lomavisCreator.postBoost.cancel')}
        handleClose={handleCancelDelete}
        handlePrimary={handleConfirmDelete}
        handleSecondary={handleCancelDelete}
      />
    </ModalBody>
  );
};
